import React, { useEffect, useState } from "react";

// ---------- Types ----------
interface CommunityData {
    community_centres: number;
    quranic_schools: number;
    water_kiosks: number;
    small_shops: number;
    sanitation_facilities: number;
    markets: number;
    community_health_centres: number;
    other_community_facility_count: number;
}

interface HumanitarianData {
    schools: number;
    health_centres: number;
    distribution_centres: number;
    protection_counselling: number;
    solar_lights: number;
    water_points: number;
    latrines: number;
    nutrition_centres: number;
    psn_houses: number;
    other_humanitarian_facility_count: number;
}

type AmenityConfig = {
    icon: string;
    label: string;
};

// ---------- Config ----------
const communityConfig: Record<keyof CommunityData, AmenityConfig> = {
    community_centres: { icon: "🏢", label: "Community Centres" },
    quranic_schools: { icon: "📖", label: "Quranic Schools" },
    water_kiosks: { icon: "💧", label: "Water Kiosks" },
    small_shops: { icon: "🏪", label: "Small Shops" },
    sanitation_facilities: { icon: "🚽", label: "Sanitation" },
    markets: { icon: "🛒", label: "Markets" },
    community_health_centres: { icon: "🏥", label: "Health Centres" },
    other_community_facility_count: { icon: "➕", label: "Other Facilities" },
};

const humanitarianConfig: Record<keyof HumanitarianData, AmenityConfig> = {
    schools: { icon: "🏫", label: "Schools" },
    health_centres: { icon: "🏥", label: "Health Centres" },
    distribution_centres: { icon: "📦", label: "Distribution Centres" },
    protection_counselling: { icon: "🛡️", label: "Protection Counselling" },
    solar_lights: { icon: "☀️", label: "Solar Lights" },
    water_points: { icon: "🚰", label: "Water Points" },
    latrines: { icon: "🚻", label: "Latrines" },
    nutrition_centres: { icon: "🥣", label: "Nutrition Centres" },
    psn_houses: { icon: "🏠", label: "PSN Houses" },
    other_humanitarian_facility_count: { icon: "➕", label: "Other Facilities" },
};

// ---------- Styles (inline) ----------
const styles = {
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2.5rem",
        padding: "1.5rem",
    } as React.CSSProperties,

    categoryBox: {
        position: "relative",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "1.75rem 1.5rem 2.25rem",
        background: "#ffffff",
        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
    } as React.CSSProperties,

    categoryLabel: {
        position: "absolute",
        bottom: "-0.7rem",
        left: "1.25rem",
        background: "#ffffff",
        padding: "0 0.75rem",
        fontSize: "0.95rem",
        fontWeight: 600,
        color: "#1e293b",
        letterSpacing: "0.01em",
    } as React.CSSProperties,

    amenitiesGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: "1rem",
    } as React.CSSProperties,

    amenityItem: {
        display: "flex",
        alignItems: "center",
        gap: "0.65rem",
        padding: "0.6rem 0.75rem",
        borderRadius: "8px",
        background: "#f8fafc",
    } as React.CSSProperties,

    amenityItemZero: {
        opacity: 0.45,
    } as React.CSSProperties,

    amenityIcon: {
        fontSize: "1.35rem",
        width: "1.75rem",
        textAlign: "center",
        flexShrink: 0,
    } as React.CSSProperties,

    amenityInfo: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    } as React.CSSProperties,

    amenityName: {
        fontSize: "0.78rem",
        color: "#64748b",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    } as React.CSSProperties,

    amenityCount: {
        fontSize: "1.05rem",
        fontWeight: 700,
        color: "#0f172a",
        lineHeight: 1.2,
    } as React.CSSProperties,
};

// ---------- Sub-component ----------
interface AmenityGridProps<T extends Record<keyof T, number>> {
    data: T | null;
    config: Record<keyof T, AmenityConfig>;
}

function AmenityGrid<T extends Record<keyof T, number>>({
    data,
    config,
}: AmenityGridProps<T>) {
    if (!data) return null;

    return (
        <div style={styles.amenitiesGrid}>
            {(Object.keys(config) as Array<keyof T>).map((key) => {
                const count = data[key] ?? 0;
                const meta = config[key];

                return (
                    <div
                        key={String(key)}
                        style={{
                            ...styles.amenityItem,
                            ...(count === 0 ? styles.amenityItemZero : {}),
                        }}
                    >
                        <div style={styles.amenityIcon}>{meta.icon}</div>
                        <div style={styles.amenityInfo}>
                            <div style={styles.amenityName}>{meta.label}</div>
                            <div style={styles.amenityCount}>{count.toLocaleString()}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ---------- Main Component ----------
const InfrastructureDashboard: React.FC = () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const [communityData, setCommunityData] = useState<CommunityData | null>(null);
    const [humanitarianData, setHumanitarianData] = useState<HumanitarianData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [communityRes, humanitarianRes] = await Promise.all([
                    fetch(`${BASE_URL}/infrastructure/community/`),
                    fetch(`${BASE_URL}/infrastructure/humanitarian/`),
                ]);

                if (!communityRes.ok || !humanitarianRes.ok) {
                    throw new Error("Failed to fetch infrastructure data");
                }

                const community = await communityRes.json();
                const humanitarian = await humanitarianRes.json();

                setCommunityData(community);
                setHumanitarianData(humanitarian);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    if (loading) {
        return <div style={{ padding: "2rem" }}>Loading infrastructure data...</div>;
    }

    if (error) {
        return (
            <div style={{ padding: "2rem", color: "#dc2626" }}>
                Error: {error}
            </div>
        );
    }

    // Simple skeleton that matches the grid look
    const SkeletonGrid = () => (
        <div style={styles.amenitiesGrid}>
            {Array.from({ length: 8 }).map((_, i) => (
                <div
                    key={i}
                    style={{
                        ...styles.amenityItem,
                        height: "52px",
                        background: "#f1f5f9",
                    }}
                />
            ))}
        </div>
    );

    return (
        <div style={styles.container}>
            {/* Humanitarian Box – always rendered */}
            <div style={styles.categoryBox}>
                {loading ? (
                    <SkeletonGrid />
                ) : error ? (
                    <div style={{ color: "#dc2626", fontSize: "0.9rem" }}>Error: {error}</div>
                ) : (
                    <AmenityGrid data={humanitarianData} config={humanitarianConfig} />
                )}
                <div style={styles.categoryLabel}>Humanitarian Infrastructure Destroyed</div>
            </div>

            {/* Community Box – always rendered */}
            <div style={styles.categoryBox}>
                {loading ? (
                    <SkeletonGrid />
                ) : error ? (
                    <div style={{ color: "#dc2626", fontSize: "0.9rem" }}>Error: {error}</div>
                ) : (
                    <AmenityGrid data={communityData} config={communityConfig} />
                )}
                <div style={styles.categoryLabel}>Community Infrastructure Destroyed</div>
            </div>
        </div>

    );
};

export default InfrastructureDashboard;